/**
 * Per-layer animation preset catalog. Behaviour lives in
 * lib/video/animationPresets.ts; this file is labels, defaults and scope.
 */

import type {
  AnimationChannel,
  AnimationPresetDef,
  AnimationPresetId,
} from '@canvas/shared-types';

export const ANIMATION_PRESETS: AnimationPresetDef[] = [
  // —— in ——
  {
    id: 'fade-in',
    label: 'Fade',
    channel: 'in',
    defaultDurationMs: 600,
    defaultEasing: 'ease-out',
    supportsDirection: false,
    touches: ['opacity'],
    hint: 'Fade up from transparent.',
    status: 'shipped',
  },
  {
    id: 'slide-in',
    label: 'Slide',
    channel: 'in',
    defaultDurationMs: 700,
    defaultEasing: 'ease-out',
    supportsDirection: true,
    touches: ['left', 'top'],
    hint: 'Slide in from a side.',
    status: 'shipped',
  },
  {
    id: 'rise',
    label: 'Rise',
    channel: 'in',
    defaultDurationMs: 700,
    defaultEasing: 'ease-out',
    supportsDirection: false,
    touches: ['top', 'opacity'],
    hint: 'Drift up while fading in.',
    status: 'shipped',
  },
  {
    id: 'pop-in',
    label: 'Pop',
    channel: 'in',
    defaultDurationMs: 500,
    defaultEasing: 'back-out',
    supportsDirection: false,
    touches: ['left', 'top', 'scaleX', 'scaleY', 'opacity'],
    hint: 'Scale up from nothing with a little bounce.',
    status: 'shipped',
  },
  {
    id: 'pan-in',
    label: 'Pan',
    channel: 'in',
    defaultDurationMs: 900,
    defaultEasing: 'ease-in-out',
    supportsDirection: true,
    touches: ['left', 'top', 'opacity'],
    hint: 'Short glide in with a fade.',
    status: 'shipped',
  },
  {
    id: 'wipe-in',
    label: 'Wipe',
    channel: 'in',
    defaultDurationMs: 600,
    defaultEasing: 'ease-out',
    supportsDirection: true,
    touches: ['clipPath'],
    hint: 'Reveal behind a moving edge.',
    status: 'planned',
  },
  {
    id: 'blur-in',
    label: 'Blur',
    channel: 'in',
    defaultDurationMs: 600,
    defaultEasing: 'ease-out',
    supportsDirection: false,
    touches: ['opacity'],
    hint: 'Sharpen into focus.',
    status: 'planned',
  },
  {
    id: 'typewriter',
    label: 'Typewriter',
    channel: 'in',
    defaultDurationMs: 1200,
    defaultEasing: 'linear',
    supportsDirection: false,
    touches: ['text'],
    hint: 'Type text in letter by letter.',
    status: 'planned',
  },

  // —— out ——
  {
    id: 'fade-out',
    label: 'Fade',
    channel: 'out',
    defaultDurationMs: 500,
    defaultEasing: 'ease-in',
    supportsDirection: false,
    touches: ['opacity'],
    hint: 'Fade to transparent.',
    status: 'shipped',
  },
  {
    id: 'slide-out',
    label: 'Slide',
    channel: 'out',
    defaultDurationMs: 600,
    defaultEasing: 'ease-in',
    supportsDirection: true,
    touches: ['left', 'top'],
    hint: 'Slide off toward a side.',
    status: 'shipped',
  },
  {
    id: 'sink',
    label: 'Sink',
    channel: 'out',
    defaultDurationMs: 600,
    defaultEasing: 'ease-in',
    supportsDirection: false,
    touches: ['top', 'opacity'],
    hint: 'Drop down while fading out.',
    status: 'shipped',
  },
  {
    id: 'pop-out',
    label: 'Pop',
    channel: 'out',
    defaultDurationMs: 400,
    defaultEasing: 'ease-in',
    supportsDirection: false,
    touches: ['left', 'top', 'scaleX', 'scaleY', 'opacity'],
    hint: 'Shrink away to nothing.',
    status: 'shipped',
  },
  {
    id: 'pan-out',
    label: 'Pan',
    channel: 'out',
    defaultDurationMs: 800,
    defaultEasing: 'ease-in-out',
    supportsDirection: true,
    touches: ['left', 'top', 'opacity'],
    hint: 'Short glide out with a fade.',
    status: 'shipped',
  },
  {
    id: 'wipe-out',
    label: 'Wipe',
    channel: 'out',
    defaultDurationMs: 600,
    defaultEasing: 'ease-in',
    supportsDirection: true,
    touches: ['clipPath'],
    hint: 'Hide behind a moving edge.',
    status: 'planned',
  },
  {
    id: 'blur-out',
    label: 'Blur',
    channel: 'out',
    defaultDurationMs: 600,
    defaultEasing: 'ease-in',
    supportsDirection: false,
    touches: ['opacity'],
    hint: 'Blur out of focus.',
    status: 'planned',
  },

  // —— emphasis ——
  {
    id: 'ken-burns',
    label: 'Ken Burns',
    channel: 'emphasis',
    defaultDurationMs: 5000,
    defaultEasing: 'smoothstep',
    supportsDirection: false,
    touches: ['left', 'top', 'scaleX', 'scaleY'],
    hint: 'Slow push-in across the scene.',
    status: 'shipped',
  },
  {
    id: 'breathe',
    label: 'Breathe',
    channel: 'emphasis',
    defaultDurationMs: 3000,
    defaultEasing: 'linear',
    supportsDirection: false,
    touches: ['left', 'top', 'scaleX', 'scaleY'],
    hint: 'Gentle, slow scale in and out.',
    status: 'shipped',
  },
  {
    id: 'float',
    label: 'Float',
    channel: 'emphasis',
    defaultDurationMs: 3000,
    defaultEasing: 'linear',
    supportsDirection: false,
    touches: ['top'],
    hint: 'Bob softly up and down.',
    status: 'shipped',
  },
  {
    id: 'pulse',
    label: 'Pulse',
    channel: 'emphasis',
    defaultDurationMs: 1000,
    defaultEasing: 'linear',
    supportsDirection: false,
    touches: ['left', 'top', 'scaleX', 'scaleY'],
    hint: 'Quick heartbeat scale.',
    status: 'shipped',
  },
  {
    id: 'wiggle',
    label: 'Wiggle',
    channel: 'emphasis',
    defaultDurationMs: 800,
    defaultEasing: 'linear',
    supportsDirection: false,
    touches: ['angle'],
    hint: 'Small side-to-side rotation.',
    status: 'shipped',
  },
  {
    id: 'spin-slow',
    label: 'Spin',
    channel: 'emphasis',
    defaultDurationMs: 6000,
    defaultEasing: 'linear',
    supportsDirection: false,
    touches: ['angle'],
    hint: 'One full slow turn per cycle.',
    status: 'shipped',
  },
];

/** Shipped presets for one channel, in catalog order. */
export function presetsByChannel(channel: AnimationChannel): AnimationPresetDef[] {
  return ANIMATION_PRESETS.filter((p) => p.channel === channel && p.status === 'shipped');
}

/** Look up a preset definition. */
export function presetDef(id: AnimationPresetId): AnimationPresetDef | undefined {
  return ANIMATION_PRESETS.find((p) => p.id === id);
}
